import React, { useEffect, useRef, useState } from 'react';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { MapPin } from 'lucide-react';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';

/**
 * A US street-address field with Google Places suggestions.
 *
 * The Outreach district lookup had this wired up already; every other place
 * that asks for an address was a plain text input. This is that behaviour as
 * a field anyone can drop in (issue #1140) — Maps loads lazily on mount, so a
 * page that never renders one never pays for the SDK.
 *
 * Selection is not mandatory: `onChange` fires on every keystroke so a typed
 * address still saves, and `onSelect` fires only when a suggestion is chosen,
 * for callers that want the resolved value (district lookup, geocoding).
 *
 * Picking a suggestion puts only the STREET in the field, not Google's full
 * description. The description is "433 Magnolia St, Denver, CO, USA", which read
 * as a street address is wrong on its face and duplicates the City/State/ZIP
 * inputs sitting under it (issue #1162). The rest of the place is handed to
 * `onSelect` so the caller can fill those fields instead. Single-field forms
 * (the citizen address ask) want the opposite — pass `selectValue: 'formatted'`
 * to keep the full description in the field.
 *
 * The logic lives in `useAddressSuggestions` so surfaces with their own design
 * system (the citizen app) can wear their own chrome over the same behaviour —
 * this component is that hook in the Pro skin.
 */

/** A picked place, split into the fields a US address form actually has. */
export interface AddressParts {
  /** Street number and route only — what belongs in a "Street Address" input. */
  street: string;
  city: string;
  /** Two-letter code, matching the value of a US state <select>. */
  state: string;
  zip: string;
  /** Google's full description, kept for callers that geocode or store it raw. */
  formatted: string;
}

export interface AddressAutocompleteProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** Called with the place split into parts when a suggestion is picked. */
  onSelect?: (parts: AddressParts) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Rendered inside the field; omit for a bare input. */
  showIcon?: boolean;
  /** Input classes. Defaults to the shared `.input`; forms whose neighbouring
      fields use their own classes pass those so the row lines up. */
  inputClassName?: string;
  /** Accessible name when no <label htmlFor> points at `id`. */
  ariaLabel?: string;
}

/**
 * Google returns the place as a flat list of typed components. Pull out the four
 * a US address form asks for; anything missing stays an empty string so a partial
 * match never overwrites a field the user already filled.
 */
interface AddressComponent {
  types: string[];
  long_name: string;
  short_name: string;
}

function parseAddressComponents(
  components: AddressComponent[] = [],
): Omit<AddressParts, 'formatted'> {
  const find = (type: string, short = false) => {
    const match = components.find((c) => c.types.includes(type));
    if (!match) return '';
    return short ? match.short_name : match.long_name;
  };

  const streetNumber = find('street_number');
  const route = find('route');

  return {
    street: [streetNumber, route].filter(Boolean).join(' '),
    // Not every place carries `locality` — smaller places label the city as a
    // sublocality or a town-level administrative area instead.
    city: find('locality') || find('sublocality_level_1') || find('administrative_area_level_3'),
    state: find('administrative_area_level_1', true),
    zip: find('postal_code'),
  };
}

export interface UseAddressSuggestionsArgs {
  value: string;
  onChange: (value: string) => void;
  /** Called with the place split into parts when a suggestion is picked. */
  onSelect?: (parts: AddressParts) => void;
  /**
   * What stays in the field after picking a suggestion: the street only
   * (split-field forms — the default, see the component comment) or Google's
   * full description (single-field forms that geocode the whole string).
   */
  selectValue?: 'street' | 'formatted';
}

/**
 * The behaviour of AddressAutocomplete without its markup: lazy Maps loading,
 * debounced Places suggestions, click-outside dismissal, and the pick-a-place
 * geocode flow. Attach `wrapperRef` to the element containing the input and
 * the suggestion list.
 */
export function useAddressSuggestions({
  value,
  onChange,
  onSelect,
  selectValue = 'street',
}: UseAddressSuggestionsArgs) {
  const { isLoaded: mapsLoaded, loadError: mapsLoadError, loadGoogleMaps } = useGoogleMapsLoader();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: { componentRestrictions: { country: 'us' }, types: ['address'] },
    debounce: 300,
    initOnMount: false,
  });

  useEffect(() => {
    if (!mapsLoaded && !mapsLoadError) loadGoogleMaps();
  }, [mapsLoaded, mapsLoadError, loadGoogleMaps]);

  useEffect(() => {
    if (mapsLoaded && !ready) init();
  }, [mapsLoaded, ready, init]);

  // Keep the hook's internal query in step with the controlled value, without
  // re-triggering a search for a value the caller just handed us.
  useEffect(() => {
    if (ready) setValue(value, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        clearSuggestions();
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [clearSuggestions]);

  const handleInput = (next: string) => {
    onChange(next);
    setValue(next);
    setShowSuggestions(true);
  };

  const handleSelect = async (placeId: string, description: string) => {
    clearSuggestions();
    setShowSuggestions(false);

    // Show the full description immediately, then narrow it to the street once
    // the geocode resolves — the field never sits empty while we wait.
    onChange(description);
    setValue(description, false);

    let parts: AddressParts = { street: '', city: '', state: '', zip: '', formatted: description };

    try {
      const [result] = await getGeocode({ placeId });
      parts = { ...parseAddressComponents(result.address_components), formatted: description };
    } catch {
      // Geocoding failed (quota, network, a place with no components). The typed
      // description is already in the field and still saves; we just cannot split it.
      onSelect?.(parts);
      return;
    }

    if (selectValue === 'street' && parts.street) {
      onChange(parts.street);
      setValue(parts.street, false);
    }
    onSelect?.(parts);
  };

  return {
    wrapperRef,
    showSuggestions,
    suggestions: status === 'OK' ? data : [],
    handleInput,
    handleSelect,
  };
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  id,
  value,
  onChange,
  onSelect,
  placeholder = 'Enter street address',
  className = '',
  disabled = false,
  showIcon = true,
  inputClassName = 'input',
  ariaLabel,
}) => {
  const { wrapperRef, showSuggestions, suggestions, handleInput, handleSelect } =
    useAddressSuggestions({ value, onChange, onSelect });

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {showIcon && (
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-4 w-4 text-text-tertiary dark:text-text-dark-tertiary" aria-hidden="true" />
        </span>
      )}
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="street-address"
        aria-label={ariaLabel}
        className={`${inputClassName} ${showIcon ? 'pl-9' : ''}`}
      />

      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-lg border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <li key={suggestion.place_id}>
              <button
                type="button"
                onClick={() => handleSelect(suggestion.place_id, suggestion.description)}
                className="w-full text-left px-3 py-2 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary"
              >
                {suggestion.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
